'use client';

import React from 'react';
import { IText, TTypographyColor } from '../Typography/Typography';
import { getSafeHtml } from '@/utils/generic';
import { usePageStore } from '@/store/usePageStore';
import { usePathname, useRouter } from 'next/navigation';
import { useFiltersStore } from '@/store/useFiltersStore';
import * as Styled from './CustomLink.styled';
import { localizedSlugs } from '@/i18n/routing';


export interface ICustomLink extends IText {
  id?: string;
  href?: string;
  slug?: string;
  hoverColor?: TTypographyColor;
  imageHoverEffect?: boolean;
  lowercase?: boolean;
  scroll?: boolean;
  prefetch?: boolean;
  callbackOnClick?: () => void;
  isEmailEncoded?: boolean;
  emailDecoded?: string;
  title?: string;
  isDisabled?: boolean;
  children?: React.ReactNode;
  saveScrollPosition?: boolean;
  target?: '_blank' | '_self';
}

export const CustomLink: React.FC<ICustomLink> = ({
  id,
  href,
  slug,
  className,
  text,
  target,
  title = '',
  size = 'default',
  color = 'default',
  scroll = true,
  prefetch = true,
  hoverColor = 'default',
  lineHeight = 'default',
  fontFamily = 'default',
  isEmailEncoded = false,
  emailDecoded = '',
  lowercase = false,
  isDisabled = false,
  saveScrollPosition = false,
  callbackOnClick,
  children,
  imageHoverEffect = false,
  ...props
}) => {
  const locale = usePageStore((s) => s.locale);
  const router = useRouter();
  const setShowNavigationModalAlert = usePageStore((s) => s.setShowNavigationModalAlert);
  const setUrlOnHoldFromPresentation = usePageStore((s) => s.setUrlOnHoldFromPresentation);
  const pathname = usePathname();
  const isPresentationMode = usePageStore((s) => s.isPresentationMode);

  const textTranslation =
    typeof text === 'object' && locale in text
      ? text[locale as keyof typeof text] || ''
      : text || '';

  const hrefTranslation =
    typeof href === 'object' && locale in href
      ? href[locale as keyof typeof href] || ''
      : href || '';

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();

    if (typeof window === 'undefined') return;

    if (isEmailEncoded && emailDecoded) {
      window.location.href = `mailto:${emailDecoded}`;
      return;
    }

    const elLink = e.currentTarget as HTMLAnchorElement;
    const targetHref = (e.currentTarget as HTMLElement)?.getAttribute('href') || slug || href;
    const projectScrollToAttribute = elLink.getAttribute('data-project-scrollto');
    const idLink = elLink.id || projectScrollToAttribute || '';

    // if (elLink.classList.contains('header-link-mobile')) {
    //   window.scrollTo({ top: 0, behavior: 'instant' });
    // }

    if (idLink && idLink.startsWith('proj-')) {
      useFiltersStore.setState({ previousClickedLinkProject: idLink });
    }

    if (saveScrollPosition) {
      const scrollPosition = window.scrollY || document.documentElement.scrollTop;
      usePageStore.getState().setScrollPosition(scrollPosition);
    }

    if (e.currentTarget?.getAttribute('target') === '_blank') {
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }

    // No store writes like previousVisitedPath or presentation gating here anymore.
    // Just navigate:
    if (isPresentationMode) {

      const isUserAllowedInPresentation = usePageStore.getState().presentationAllowedState === 'allowed' 
        || usePageStore.getState().presentationAllowedState === 'allowed_first_time';

      // Only from presentation to projects or projects to presentation
      const isNavigatingInPresentation = (
        targetHref?.startsWith(`/${localizedSlugs.projects.en}/`) ||
        targetHref?.startsWith(`/es/${localizedSlugs.projects.es}/`)
      ) || (
        targetHref?.startsWith(`/${localizedSlugs.presentation.en}/`) ||
        targetHref?.startsWith(`/es/${localizedSlugs.presentation.es}/`) ||
        targetHref?.startsWith(`/es/${localizedSlugs.presentation.en}/`)
      );
      
      if (!isNavigatingInPresentation && isUserAllowedInPresentation) {
        setShowNavigationModalAlert(true);
        setUrlOnHoldFromPresentation(targetHref);
      } else {
        usePageStore.setState({ previousVisitedPath: pathname });
        router.push(targetHref || '/');
      }
      return;
    }
    if (callbackOnClick) callbackOnClick();
    if (!targetHref || targetHref === '#') return;
    router.push(targetHref);
  };

  if (!isDisabled) {
    return text ? (
      <Styled.CustomLink
        id={id}
        href={slug || hrefTranslation || '#'}
        className={`custom-link ${className || ''}`}
        size={size}
        lineHeight={lineHeight}
        color={color}
        hoverColor={hoverColor}
        fontFamily={fontFamily}
        prefetch={prefetch}
        dangerouslySetInnerHTML={{
          __html: !lowercase
            ? getSafeHtml(textTranslation as string).replace('<a>', '')
            : getSafeHtml(textTranslation as string).toLowerCase(),
        }}
        {...(title && { title: title })}
        {...(!scroll && { scroll: false })}
        {...(target && { target: target })}
        {...props}
        onClick={(e) => handleClick(e)}
      ></Styled.CustomLink>
    ) : (
      children && (
        <Styled.ContainerLink
          id={id}
          href={slug || hrefTranslation || '#'}
          className={className}
          imageHoverEffect={imageHoverEffect}
          prefetch={prefetch}
          {...(title && { title: title })}
          {...(!scroll && { scroll: false })}
          {...(target && { target: target })}
          {...props}
          onClick={(e) => handleClick(e)}
        >
          {children}
        </Styled.ContainerLink>
      )
    );
  } else {
    return (
      <Styled.DisabledLink
        className={`${className} disabled-link`}
        id={id}
      >
        {children}
      </Styled.DisabledLink>
    );
  }
};
